/**
 * VersionList + VersionCreateForm Components -- Xyden Leadership Assessment Platform
 * Step 9F Green Phase
 *
 * Version items use DataTable with Link-based name column for navigation.
 */

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { DataTable, type Column, type RowAction } from '../DataTable';
import { StatusBadge } from '../StatusBadge';
import { Copy } from 'lucide-react';
import type { Version, BadgeVariant } from '../../types';

function getVersionVariant(state: string): BadgeVariant {
  switch (state.toLowerCase()) {
    case 'draft': return 'draft';
    case 'active': return 'active';
    case 'archived': return 'archived';
    default: return 'pending';
  }
}

export interface VersionListProps {
  versions: Version[];
  onCreateDraft?: () => void;
  onSelectVersion?: (versionId: string) => void;
  onCreateDraftFrom?: (versionId: string) => void;
  onSelect?: (version: Version) => void;
  isLoading?: boolean;
  error?: string | null;
}

export function VersionList({
  versions,
  onCreateDraft,
  onSelectVersion,
  onCreateDraftFrom,
  onSelect,
  isLoading,
  error,
}: VersionListProps) {
  const columns: Column<Version>[] = useMemo(() => [
    {
      key: 'version_name',
      header: 'Version Name',
      sortable: true,
      render: (_value, row) => (
        <Link
          to={`/content-studio/versions/${row.id}`}
          onClick={() => {
            onSelectVersion?.(row.id);
            onSelect?.(row);
          }}
          className="font-medium text-brand-active hover:underline"
        >
          {row.version_name}
        </Link>
      ),
    },
    {
      key: 'state',
      header: 'Status',
      sortable: true,
      render: (_value, row) => (
        <StatusBadge variant={getVersionVariant(row.state)}>
          {row.state}
        </StatusBadge>
      ),
    },
    {
      key: 'changelog_notes',
      header: 'Notes',
      truncate: true,
    },
  ], [onSelectVersion, onSelect]);

  const actions: RowAction<Version>[] = useMemo(() => {
    if (!onCreateDraftFrom) return [];
    return [
      {
        label: 'Create Draft from this',
        icon: <Copy size={16} aria-hidden="true" />,
        onClick: (row) => onCreateDraftFrom(row.id),
      },
    ];
  }, [onCreateDraftFrom]);

  return (
    <div>
      {onCreateDraft && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">Versions</h2>
          <Button
            onClick={onCreateDraft}
            className="bg-brand-crimson hover:bg-brand-crimson/90 text-white font-medium text-sm rounded-lg px-4 py-2"
          >
            Create Draft
          </Button>
        </div>
      )}

      <DataTable<Version>
        data={versions}
        columns={columns}
        keyField="id"
        loading={isLoading}
        error={error ?? null}
        emptyMessage="No versions found."
        actions={actions.length > 0 ? actions : undefined}
      />
    </div>
  );
}

// VersionCreateForm

export interface VersionCreateFormProps {
  onSubmit: (data: { version_name: string; changelog_notes: string; from_scratch: boolean }) => void;
  onCancel: () => void;
}

export function VersionCreateForm({ onSubmit, onCancel }: VersionCreateFormProps) {
  const [versionName, setVersionName] = useState('');
  const [notes, setNotes] = useState('');
  const [fromScratch, setFromScratch] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const isFormComplete = versionName.trim().length > 0 && notes.trim().length > 0;

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!versionName.trim()) errs.version_name = 'Version name is required';
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onSubmit({ version_name: versionName, changelog_notes: notes, from_scratch: fromScratch });
  };

  return (
    <form onSubmit={handleSubmit} aria-label="Create version form">
      <div className="mb-3">
        <label htmlFor="version-name" className="block text-sm font-medium text-gray-700 mb-1">
          Version Name <span className="text-brand-crimson">*</span>
        </label>
        <input
          id="version-name"
          type="text"
          value={versionName}
          onChange={e => { setVersionName(e.target.value); if (submitted) setErrors({}); }}
          className="w-full border border-brand-steel/50 bg-white rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-brand-steel/50 focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
          aria-invalid={Boolean(errors.version_name)}
          aria-describedby={errors.version_name ? 'version-name-error' : undefined}
        />
        {errors.version_name && (
          <>
            <span id="version-name-desc" className="sr-only">{errors.version_name}</span>
            <div id="version-name-error" role="alert" aria-describedby="version-name-desc" className="text-xs text-brand-error mt-1">
              {errors.version_name}
            </div>
          </>
        )}
      </div>
      <div className="mb-4">
        <label htmlFor="changelog-notes" className="block text-sm font-medium text-gray-700 mb-1">Changelog Notes</label>
        <textarea
          id="changelog-notes"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          className="w-full border border-brand-steel/50 bg-white rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-brand-steel/50 resize-none focus:outline-none focus:ring-2 focus:ring-brand-crimson/30 focus:border-brand-crimson"
        />
      </div>
      <div className="mb-4">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={fromScratch}
            onChange={e => setFromScratch(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-brand-steel/50 text-brand-crimson focus:ring-brand-crimson/30"
          />
          <span className="text-sm">
            <span className="font-medium text-gray-700">Start from scratch</span>
            <span className="block text-xs text-brand-steel/70 mt-0.5">
              Create an empty Draft. Skips cloning content from the active version — useful when you'll import buckets, activities, and competencies from XLSX.
            </span>
          </span>
        </label>
      </div>
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={!isFormComplete} className="bg-brand-crimson hover:bg-brand-crimson/90 text-white font-medium text-sm rounded-lg px-4 py-2">Create</Button>
      </div>
    </form>
  );
}

export default VersionList;
